/**
 * Utilities for containing support CSS from cherry-picked template sections.
 *
 * A fully loaded template can keep its own globals. When a single section is
 * imported from a different template, however, that template's global support
 * CSS must only affect sections from that source site.
 */

import * as csstree from '../vendor/csstree.esm.js';
import { getAssetURLSync, hasManifestAsset, loadAsset, loadJSON } from '../core/asset-loader.js';
import { rewriteHtmlAssetUrlsPreferVendored } from './template-asset-refs.js';
import { FONT_POLICY_VENDORED, rewriteTemplateFontRules } from './template-font-rules.js';
import { buildVendoredUri, vendoredBundleRoot } from './vendored-asset-uri.js';
import { isUsableFontFamily, parseFirstFont } from './font-utils.js';
import { runtimeMotionStateClasses } from './motion-residue.js';
import { normalizeHexColor, THEME_COLOR_KEY_SET } from './theme-tokens.js';
import { FORM_SHAPES, FORM_STYLES } from './forms-presets-data.js';
import {
  deriveButtonClasses,
  buildButtonSelector,
  detectTemplateHoverEffect
} from './button-vocabulary.js';

export const TEMPLATE_SUPPORT_MARKER_PREFIX = '/* pb-template-support:';
export const TEMPLATE_SUPPORT_ATTR = 'data-template-site';

const KEYFRAME_AT_RULES = new Set(['keyframes', '-webkit-keyframes']);
const TEMPLATE_FONT_AT_RULES = new Set(['font-face', 'import']);
export const TEMPLATE_BUTTON_CLASSES = new Set(['cs-button-solid', 'cs-button-transparent']);
export const TEMPLATE_CARD_CLASSES = new Set([
  'cs-card',
  'cs-box',
  'cs-card-inner',
  'cs-card-image',
  'cs-card-img',
  'cs-card-title',
  'cs-card-text',
  'cs-card-overlay',
  'cs-card-body',
  'cs-card-grad',
  'cs-card-meta',
  'cs-card-sub'
]);
export const TEMPLATE_INPUT_CLASSES = new Set([
  'cs-input',
  'cs-textarea',
  'cs-select',
  'cs-form',
  'cs-form-group'
]);
export const TEMPLATE_PILL_CLASSES = new Set(['cs-pill', 'cs-badge']);

function escapeCSSString(value) {
  return String(value == null ? '' : value)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\a ')
    .replace(/\r/g, '');
}

export function templateSupportAttrValue(siteName) {
  return String(siteName || '').trim();
}

export function templateSupportScopeSelector(siteName) {
  const value = templateSupportAttrValue(siteName);
  return value ? `[${TEMPLATE_SUPPORT_ATTR}="${escapeCSSString(value)}"]` : '';
}

export function sectionNeedsTemplateSupportCSS(section, sourceTemplateDomain = null) {
  const siteName = templateSupportAttrValue(section?.templateSiteName);
  const sourceName = templateSupportAttrValue(sourceTemplateDomain);
  return !!siteName && siteName !== sourceName;
}

function splitLeadingCompound(selector) {
  let depth = 0;
  for (let i = 0; i < selector.length; i++) {
    const ch = selector[i];
    if (ch === '(' || ch === '[') depth++;
    else if ((ch === ')' || ch === ']') && depth > 0) depth--;
    if (depth === 0 && (/\s/.test(ch) || ch === '>' || ch === '+' || ch === '~')) {
      return {
        first: selector.slice(0, i),
        rest: selector.slice(i).trim()
      };
    }
  }
  return { first: selector, rest: '' };
}

function documentSelectorName(compound) {
  const lower = String(compound || '')
    .trim()
    .toLowerCase();
  if (lower.startsWith('body') && !/[a-z0-9_-]/.test(lower[4] || '')) return 'body';
  if (lower.startsWith('html') && !/[a-z0-9_-]/.test(lower[4] || '')) return 'html';
  return '';
}

function scopeSelector(selector, scope) {
  const trimmed = String(selector || '').trim();
  if (!trimmed || !scope) return trimmed;
  if (trimmed === scope || trimmed.startsWith(`${scope} `) || trimmed.startsWith(`${scope}:`)) {
    return trimmed;
  }

  if (trimmed === ':root' || trimmed === 'body' || trimmed === 'html') {
    return scope;
  }

  const { first, rest } = splitLeadingCompound(trimmed);
  const docName = documentSelectorName(first);
  if (docName) {
    if (!rest) return `${first} ${scope}`;
    if (rest === scope || rest.startsWith(`${scope} `) || rest.startsWith(`${scope}:`)) {
      return trimmed;
    }
    return `${first} ${scope} ${rest}`;
  }

  return `${scope} ${trimmed}`;
}

function selectorAttrName(node) {
  if (!node || !node.name) return '';
  if (typeof node.name.name === 'string') return node.name.name;
  if (typeof node.name.value === 'string') return node.name.value;
  if (typeof node.name === 'string') return node.name;
  return '';
}

function cssMentionsClassSet(css, classSet) {
  const raw = String(css || '');
  if (!raw || !classSet?.size) return false;
  for (const className of classSet) {
    if (raw.includes(className)) return true;
  }
  return false;
}

function selectorTargetsClassSet(selector, classSet) {
  let matched = false;
  csstree.walk(selector, {
    visit: 'ClassSelector',
    enter(node) {
      if (classSet.has(node.name)) matched = true;
    }
  });
  return matched;
}

function selectorHasSectionScope(selector) {
  let matched = false;
  csstree.walk(selector, {
    visit: 'AttributeSelector',
    enter(node) {
      const attrName = selectorAttrName(node);
      if (attrName === 'data-pb-section' || attrName === TEMPLATE_SUPPORT_ATTR) matched = true;
    }
  });
  csstree.walk(selector, {
    visit: 'IdSelector',
    enter() {
      matched = true;
    }
  });
  return matched;
}

function removeMarkedTemplateSupportBlocks(css) {
  const raw = String(css || '');
  if (!raw.includes(TEMPLATE_SUPPORT_MARKER_PREFIX)) return raw;

  let out = '';
  let cursor = 0;
  while (cursor < raw.length) {
    const start = raw.indexOf(TEMPLATE_SUPPORT_MARKER_PREFIX, cursor);
    if (start === -1) {
      out += raw.slice(cursor);
      break;
    }

    const startEnd = raw.indexOf('*/', start);
    if (startEnd === -1) {
      out += raw.slice(cursor);
      break;
    }

    const siteName = raw.slice(start + TEMPLATE_SUPPORT_MARKER_PREFIX.length, startEnd).trim();
    const endMarker = `/* /pb-template-support:${siteName} */`;
    const end = raw.indexOf(endMarker, startEnd + 2);
    if (!siteName || end === -1) {
      out += raw.slice(cursor);
      break;
    }

    out += raw.slice(cursor, start);
    cursor = end + endMarker.length;
  }
  return out;
}

function pruneClassSetRulesFromBlock(block, classSet) {
  if (!block?.children?.forEach) return;

  block.children.forEach((child, item) => {
    if (child.type === 'Rule' && child.prelude?.type === 'SelectorList') {
      child.prelude.children.forEach((selector, selectorItem) => {
        if (selectorTargetsClassSet(selector, classSet)) {
          child.prelude.children.remove(selectorItem);
        }
      });

      if (child.prelude.children.isEmpty) {
        block.children.remove(item);
      }
      return;
    }

    if (child.type === 'Atrule' && child.block) {
      const atName = String(child.name || '').toLowerCase();
      if (KEYFRAME_AT_RULES.has(atName) || TEMPLATE_FONT_AT_RULES.has(atName)) return;
      pruneClassSetRulesFromBlock(child.block, classSet);
      if (child.block.children?.isEmpty) {
        block.children.remove(item);
      }
    }
  });
}

export function stripTemplateComponentRules(css, classSet) {
  const raw = String(css || '');
  if (!cssMentionsClassSet(raw, classSet)) {
    return raw;
  }

  let ast;
  try {
    ast = csstree.parse(raw);
  } catch (err) {
    console.warn('[template-support-css] component rule strip parse failed:', err.message);
    return raw;
  }

  pruneClassSetRulesFromBlock(ast, classSet);

  try {
    return csstree.generate(ast);
  } catch (err) {
    console.warn('[template-support-css] component rule strip generate failed:', err.message);
    return raw;
  }
}

export function stripTemplateButtonRules(css) {
  return stripTemplateComponentRules(css, TEMPLATE_BUTTON_CLASSES);
}

export function hasGlobalTemplateComponentRules(css, classSet) {
  const raw = removeMarkedTemplateSupportBlocks(css);
  if (!cssMentionsClassSet(raw, classSet)) {
    return false;
  }

  let ast;
  try {
    ast = csstree.parse(raw);
  } catch (err) {
    console.warn('[template-support-css] component rule detect parse failed:', err.message);
    return false;
  }

  let matched = false;
  csstree.walk(ast, {
    visit: 'Rule',
    enter(node) {
      if (matched || node.prelude?.type !== 'SelectorList' || !node.prelude.children) return;
      node.prelude.children.forEach((selector) => {
        if (matched) return;
        if (selectorTargetsClassSet(selector, classSet) && !selectorHasSectionScope(selector)) {
          matched = true;
        }
      });
    }
  });
  return matched;
}

export function hasGlobalTemplateButtonRules(css) {
  return hasGlobalTemplateComponentRules(css, TEMPLATE_BUTTON_CLASSES);
}

export function scopeTemplateSupportCSS(css, siteName) {
  const raw = String(css || '').trim();
  const scope = templateSupportScopeSelector(siteName);
  if (!raw || !scope) return raw;

  let ast;
  try {
    ast = csstree.parse(raw);
  } catch (err) {
    console.warn('[template-support-css] parse failed:', err.message);
    return raw;
  }

  csstree.walk(ast, {
    visit: 'Rule',
    enter(node) {
      if (this.atrule && KEYFRAME_AT_RULES.has(String(this.atrule.name || '').toLowerCase())) {
        return;
      }
      if (node.prelude?.type !== 'SelectorList' || !node.prelude.children) return;

      const scopedSelectors = [];
      node.prelude.children.forEach((selector) => {
        scopedSelectors.push(scopeSelector(csstree.generate(selector), scope));
      });

      try {
        node.prelude = csstree.parse(scopedSelectors.join(','), { context: 'selectorList' });
      } catch (err) {
        console.warn('[template-support-css] selector rewrite failed:', err.message);
      }
    }
  });

  try {
    return csstree.generate(ast);
  } catch (err) {
    console.warn('[template-support-css] generate failed:', err.message);
    return raw;
  }
}

/**
 * Scope only the marked support blocks inside the assembled template CSS.
 * The unmarked leading template CSS remains untouched because it belongs
 * to the starting template (primary domain) for the current project.
 */
export function scopeTemplateSupportBlocks(templateCSS) {
  const css = String(templateCSS || '');
  if (!css.includes(TEMPLATE_SUPPORT_MARKER_PREFIX)) return css;

  let out = '';
  let cursor = 0;

  while (cursor < css.length) {
    const start = css.indexOf(TEMPLATE_SUPPORT_MARKER_PREFIX, cursor);
    if (start === -1) {
      out += css.slice(cursor);
      break;
    }

    const startEnd = css.indexOf('*/', start);
    if (startEnd === -1) {
      out += css.slice(cursor);
      break;
    }

    const siteName = css.slice(start + TEMPLATE_SUPPORT_MARKER_PREFIX.length, startEnd).trim();
    const endMarker = `/* /pb-template-support:${siteName} */`;
    const innerStart = startEnd + 2;
    const end = css.indexOf(endMarker, innerStart);
    if (!siteName || end === -1) {
      out += css.slice(cursor);
      break;
    }

    const startComment = css.slice(start, startEnd + 2);
    const inner = css.slice(innerStart, end);
    out += css.slice(cursor, start);
    out += `${startComment}\n${scopeTemplateSupportCSS(inner, siteName)}\n${endMarker}`;
    cursor = end + endMarker.length;
  }

  return out;
}

/**
 * Rewrite root-relative `url(/path)` references in a CSS string so they point
 * at the source template's live origin. The scraper rewrote HTML `src=` to
 * absolute URLs but missed CSS `url()` values, leaving e.g.
 * `url(/assets/images/fin.svg)` pointing nowhere inside the builder iframe.
 *
 * Skips: already-absolute (http://, https://, //protocol-relative), data:, blob:,
 * pb-asset:, ./relative, #fragments, and any url() whose value doesn't start with
 * a single forward slash.
 */
export function rewriteCssAssetUrls(css, sourceDomain) {
  if (!css || !sourceDomain) return String(css || '');
  const domain = String(sourceDomain)
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
  if (!domain) return String(css);
  // url( optional whitespace, optional quote, single / not followed by another /,
  // then path chars, matching quote, closing paren.
  return String(css).replace(
    /url\(\s*(['"]?)\s*(\/(?!\/)[^'")\s]*)\s*\1\s*\)/g,
    (_, q, path) => `url(${q}https://${domain}${path}${q})`
  );
}

/**
 * The same rewrite, but a VENDORED BYTE WINS.
 *
 * `rewriteCssAssetUrls` above sends every root-relative image in template CSS
 * to `https://<templateSiteName><path>` — a hotlink to somebody else's server,
 * for the life of the template. That is fine right up until the origin moves,
 * and then it is silent: abshillbillygyros.com's bare host stopped resolving,
 * its hero background became nothing, and `#f5f3f3` hero text rendered white
 * on white. The bytes were on disk the whole time.
 *
 * So: check the reference bundle first and emit `pb-vendored://` when it has
 * the file. Every consumer of template CSS already resolves that scheme (the
 * preview to a manifest URL, the saved preview to a data: URI, the exporters
 * to a bundled path) because the font work needed it — this only gives it more
 * to resolve.
 *
 * THE FALLBACK IS THE OLD BEHAVIOUR, NOT `none`. `rewriteTemplateAssetUrls`
 * blanks an unvendored relative image, which is right where it is used (a
 * font-policy site's own stylesheet, whose assets are all supposed to be
 * present) and would be a regression here: 158 of 372 domains have never
 * vendored at all, and blanking their images to fix one site is a bad trade.
 * Worst case this function does exactly what the old one did.
 *
 * @param {string} css
 * @param {string} sourceDomain — the template directory name
 * @param {Record<string,string>} [vendoredAssets] — absolute source URL → rel path
 */
export function rewriteCssAssetUrlsPreferVendored(css, sourceDomain, vendoredAssets) {
  const raw = String(css || '');
  if (!raw || !sourceDomain || !/url\(/i.test(raw)) return rewriteCssAssetUrls(raw, sourceDomain);
  const map = vendoredAssets && typeof vendoredAssets === 'object' ? vendoredAssets : null;
  if (!map || Object.keys(map).length === 0) return rewriteCssAssetUrls(raw, sourceDomain);

  const domain = String(sourceDomain)
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
  if (!domain) return raw;

  // Every url(), not only the root-relative ones: an ABSOLUTE url to the source
  // origin is the same hostage to the same server, and the bundle keys are
  // absolute, so those are the ones most likely to match.
  const out = raw.replace(/url\(\s*(['"]?)\s*([^'")\s]+)\s*\1\s*\)/gi, (match, quote, value) => {
    const url = String(value || '').trim();
    if (!url || /^(?:data:|blob:|pb-asset:|pb-vendored:|#)/i.test(url)) return match;
    if (!TEMPLATE_CSS_IMAGE_EXT_RE.test(stripCssUrlFragment(url))) return match;
    const rel = lookupVendoredTemplateAssetRel(url, domain, map);
    if (!rel) return match;
    const vendored = buildVendoredUri(domain, rel);
    return vendored ? `url(${quote}${vendored}${quote})` : match;
  });

  // Whatever did not vendor still needs absolutizing, exactly as before.
  return rewriteCssAssetUrls(out, domain);
}

const TEMPLATE_CSS_IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|avif|svg|ico|bmp)(?:[?#]|$)/i;

function stripCssUrlFragment(value) {
  return String(value || '')
    .split('#')[0]
    .replace(/\?$/, '');
}

function absolutizeTemplateAssetUrl(value, domain) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('//')) return `https:${raw}`;
  const host = String(domain || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
  if (!host) return '';
  if (raw.startsWith('/')) return `https://${host}${raw}`;
  return '';
}

function relativeTemplateAssetKey(value) {
  const raw = stripCssUrlFragment(value).trim();
  if (!raw) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//') || raw.startsWith('/')) return '';
  return raw.replace(/^(?:\.\.\/|\.\/)+/g, '').replace(/^\/+/, '');
}

function manifestTemplateAssetKey(value) {
  try {
    const parsed = new URL(stripCssUrlFragment(value));
    return `${parsed.pathname.replace(/^\/+/, '')}${parsed.search}`;
  } catch {
    return '';
  }
}

/**
 * The `www.` question.
 *
 * The manifest keys a vendored asset by the URL the CRAWLER fetched, which is
 * whatever host the source site actually serves on; the render path
 * absolutizes with `templateSiteName`, which is the template DIRECTORY name.
 * On abshillbillygyros.com those differ — the bundle holds
 * `https://www.abshillbillygyros.com/images/landing.jpg` and the lookup asked
 * for the bare host — so a hero background that was sitting on disk was
 * declared missing and fell through to a hotlink at a domain that no longer
 * resolves. Nine templates in the corpus have this split.
 *
 * Both directions, because either half can be the one with the prefix.
 */
function hostVariantsOf(url) {
  const raw = String(url || '');
  const out = [raw];
  if (/^https?:\/\/www\./i.test(raw)) out.push(raw.replace(/^(https?:\/\/)www\./i, '$1'));
  else if (/^https?:\/\//i.test(raw)) out.push(raw.replace(/^(https?:\/\/)/i, '$1www.'));
  return out;
}

/**
 * Public form of the lookup below, for the HTML rewrite paths.
 *
 * Returns the bundle-relative `vendored/<hash>.<ext>` for this domain, or `''`.
 * Ownership is the PAIR `(domain, url)`: a URL vendored in some other template's
 * bundle is not resolvable from this one, and asking a global set would report
 * coverage this domain does not have.
 */
export function lookupVendoredAssetRel(original, domain, vendoredAssets) {
  const map = vendoredAssets && typeof vendoredAssets === 'object' ? vendoredAssets : null;
  if (!map || !original) return '';
  const clean = String(domain || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
  if (!clean) return '';
  return lookupVendoredTemplateAssetRel(String(original), clean, map);
}

function lookupVendoredTemplateAssetRel(original, domain, map) {
  const absolute = absolutizeTemplateAssetUrl(original, domain);
  if (absolute) {
    for (const candidate of hostVariantsOf(absolute)) {
      const hit = map[candidate] || map[stripCssUrlFragment(candidate)];
      if (hit) return hit;
    }
    // FALLS THROUGH, deliberately. This used to `return` here whether or not it
    // matched, so an absolutizable URL never reached the path-suffix matcher
    // below — which is exactly the rescue an unexpected host needs. The suffix
    // matcher is already conservative (it refuses an ambiguous match), so
    // letting it see these costs nothing and recovers the rest.
  }

  const suffix = relativeTemplateAssetKey(original) || pathSuffixOf(original);
  if (!suffix) return '';

  const matches = new Set();
  for (const [source, rel] of Object.entries(map)) {
    const key = manifestTemplateAssetKey(source);
    if (key === suffix || key.endsWith(`/${suffix}`)) matches.add(rel);
  }
  // One match or nothing. Two different vendored files whose paths both end
  // `/logo.png` are two different pictures, and picking either is a coin flip
  // that renders the wrong image with no error anywhere.
  return matches.size === 1 ? [...matches][0] : '';
}

/** `/images/landing.jpg` → `images/landing.jpg`, so a root-relative url can
 *  reach the suffix matcher too. Absolute URLs keep their own path. */
function pathSuffixOf(value) {
  const raw = stripCssUrlFragment(value).trim();
  if (!raw) return '';
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw.replace(/^\/+/, '');
  if (/^https?:\/\//i.test(raw)) return manifestTemplateAssetKey(raw);
  return '';
}

function isRelativeTemplateAssetUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (/^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i.test(raw)) return false;
  return true;
}

export function rewriteTemplateAssetUrls(css, { domain, vendoredAssets } = {}) {
  const raw = String(css || '');
  if (!raw || !/url\(/i.test(raw)) return raw;
  const map = vendoredAssets && typeof vendoredAssets === 'object' ? vendoredAssets : {};
  return raw.replace(/url\(\s*(['"]?)\s*([^'")\s]+)\s*\1\s*\)/gi, (match, quote, value) => {
    const url = String(value || '').trim();
    if (!url || /^(?:data:|blob:|pb-asset:|pb-vendored:)/i.test(url)) return match;
    if (!TEMPLATE_CSS_IMAGE_EXT_RE.test(stripCssUrlFragment(url))) return match;

    const rel = lookupVendoredTemplateAssetRel(url, domain, map);
    const vendored = rel ? buildVendoredUri(domain, rel) : '';
    if (vendored) return `url(${quote}${vendored}${quote})`;

    // A stylesheet-relative image that did not vendor will resolve against the
    // builder's temporary render file and fail as a bogus file:// request. The
    // source asset is already absent from the reference bundle, so render it as
    // an intentionally empty image instead of a live-origin/temp-file request.
    if (isRelativeTemplateAssetUrl(url)) return 'none';
    return match;
  });
}

/**
 * Selector vocabulary for heading-like elements across CodeStitch + scraped
 * templates. Mirrors the heading entries in EDITABLE_MAP.text at
 * editable-utils.js:14-17 plus scraped-template additions surfaced in audits.
 * Keep in sync with that list when it grows.
 */
export const HEADING_SELECTOR_RE =
  /\b(?:h[1-6]|cs-(?:title|h[1-6]|heading|heading3|topper|header|cta-title|form-title|review-title|item-title|item-header|box-header|banner-title))\b/;

/**
 * CSS-wide keywords are the only unambiguous "do not touch" markers for
 * font-family values. Generic family names (sans-serif, system-ui, etc.) are
 * intentionally NOT in this set; a section declaring those instead of a brand
 * font should still pick up the project theme. Section authors who *want* the
 * generic stack as design intent opt out via section.preserveTypography.
 */
const SKIP_FONT_VALUES = new Set(['inherit', 'initial', 'unset', 'revert', 'revert-layer']);

/**
 * Wrap every hardcoded `font-family: <stack>` declaration in section CSS with
 * `var(--pb-heading-font, <stack>)` (heading selectors) or
 * `var(--pb-body-font, <stack>)` (everything else). The theme vars are emitted
 * by the iframe renderer (preview) and generateRootCSS (export) so they're
 * always defined; the original stack remains as the fallback for safety.
 *
 * Skips:
 * - Values already wrapped in `var(...)` (idempotent on second pass).
 * - CSS-wide keywords (`inherit`, `initial`, `unset`, `revert`, `revert-layer`).
 * - Malformed CSS (returns input unchanged after a parse-failure warning).
 */
export function normalizeSectionTypography(css) {
  if (!css || typeof css !== 'string') return css || '';
  if (!/font-family/i.test(css)) return css;

  let ast;
  try {
    ast = csstree.parse(css);
  } catch (err) {
    console.warn('[template-support-css] typography normalize parse failed:', err.message);
    return css;
  }

  csstree.walk(ast, {
    visit: 'Rule',
    enter(rule) {
      if (rule.prelude?.type !== 'SelectorList' || !rule.prelude.children) return;
      const selectorText = csstree.generate(rule.prelude).toLowerCase();
      const isHeading = HEADING_SELECTOR_RE.test(selectorText);

      if (!rule.block?.children) return;
      rule.block.children.forEach((decl) => {
        if (decl?.type !== 'Declaration') return;
        if (String(decl.property || '').toLowerCase() !== 'font-family') return;

        const raw = csstree.generate(decl.value).trim();
        if (!raw) return;
        if (raw.toLowerCase().startsWith('var(')) return;
        if (SKIP_FONT_VALUES.has(raw.toLowerCase())) return;

        const cssVar = isHeading ? '--pb-heading-font' : '--pb-body-font';
        try {
          decl.value = csstree.parse(`var(${cssVar}, ${raw})`, { context: 'value' });
        } catch (err) {
          console.warn('[template-support-css] could not wrap font-family value:', err.message);
        }
      });
    }
  });

  try {
    return csstree.generate(ast);
  } catch (err) {
    console.warn('[template-support-css] typography normalize generate failed:', err.message);
    return css;
  }
}

/**
 * Extract just the typography-related CSS custom properties from a template's
 * `_styles.css` `:root` block. Templates declare per-site font roles such as
 * `--bodyFontFamily`, `--headerFontFamily`, `--firstFontTitle` (Casa Ridge uses
 * three distinct fonts) and section CSS references them via `var()`. Without
 * these declarations the references go undefined and headings/titles fall back
 * to the inherited body font.
 *
 * Only `--*Font*Family` and `--*FontTitle` declarations are extracted (case-
 * insensitive). Color and size vars are left alone so the user's project theme
 * (state.theme) remains the source of truth for those.
 *
 * Returns an empty string when no matching declarations are found.
 */
export function extractTemplateFontFamilyVars(css) {
  const raw = String(css || '');
  if (!raw || !/:root/i.test(raw)) return '';

  let ast;
  try {
    ast = csstree.parse(raw);
  } catch (err) {
    console.warn('[template-support-css] font var extract parse failed:', err.message);
    return '';
  }

  const decls = [];
  const seen = new Set();
  const FONT_VAR_RE = /^--[a-zA-Z]*font(family|title)/i;

  csstree.walk(ast, {
    visit: 'Rule',
    enter(rule) {
      if (rule.prelude?.type !== 'SelectorList') return;
      const selectorText = csstree.generate(rule.prelude).trim();
      if (!/(^|,)\s*:root\s*($|,)/i.test(selectorText)) return;
      if (!rule.block?.children) return;
      rule.block.children.forEach((decl) => {
        if (decl?.type !== 'Declaration') return;
        const prop = String(decl.property || '');
        if (!FONT_VAR_RE.test(prop)) return;
        if (seen.has(prop)) return;
        seen.add(prop);
        const val = csstree.generate(decl.value).trim();
        if (!val) return;
        decls.push(`  ${prop}: ${val};`);
      });
    }
  });

  if (decls.length === 0) return '';
  return `:root {\n${decls.join('\n')}\n}`;
}

/**
 * Recover selectors whose classes are mutated by the shared scroll runtime
 * from the preserved full stylesheet. Early DOM-attributed ingests could move
 * an end-state rule into only the section visible in the crawl snapshot; their
 * `_styles.css` remains complete, so this compatibility closure repairs those
 * existing bundles while the parser-side guard prevents future bad splits.
 */
export function extractTemplateMotionStateRules(css) {
  const raw = String(css || '');
  if (!raw) return '';
  const stateClasses = runtimeMotionStateClasses();
  let ast;
  try {
    ast = csstree.parse(raw);
  } catch (err) {
    console.warn('[template-support-css] motion state extract parse failed:', err.message);
    return '';
  }

  const selectorUsesState = (selector) => {
    let matched = false;
    csstree.walk(selector, {
      visit: 'ClassSelector',
      enter(node) {
        if (stateClasses.has(node.name)) matched = true;
      }
    });
    return matched;
  };

  const prune = (block) => {
    if (!block?.children?.forEach) return;
    block.children.forEach((child, item) => {
      if (child.type === 'Rule' && child.prelude?.type === 'SelectorList') {
        child.prelude.children.forEach((selector, selectorItem) => {
          if (!selectorUsesState(selector)) child.prelude.children.remove(selectorItem);
        });
        if (child.prelude.children.isEmpty) block.children.remove(item);
        return;
      }
      if (child.type === 'Atrule' && child.block) {
        prune(child.block);
        if (child.block.children?.isEmpty) block.children.remove(item);
        return;
      }
      block.children.remove(item);
    });
  };

  prune(ast);
  try {
    return csstree.generate(ast);
  } catch (err) {
    console.warn('[template-support-css] motion state extract generate failed:', err.message);
    return '';
  }
}

export function collectTemplateFontFamilies(css) {
  const raw = String(css || '');
  if (!raw || !/font/i.test(raw)) return [];

  let ast;
  try {
    ast = csstree.parse(raw, { parseCustomProperty: true });
  } catch (err) {
    console.warn('[template-support-css] font family collect parse failed:', err.message);
    return [];
  }

  const families = [];
  const seen = new Set();
  const FONT_VAR_RE = /^--[a-zA-Z]*font(family|title)?$/i;

  function addFamily(value) {
    const family = parseFirstFont(value);
    if (!isUsableFontFamily(family) || seen.has(family)) return;
    seen.add(family);
    families.push(family);
  }

  csstree.walk(ast, {
    visit: 'Declaration',
    enter(decl) {
      const prop = String(decl.property || '');
      if (prop === 'font-family' || FONT_VAR_RE.test(prop)) {
        addFamily(csstree.generate(decl.value).trim());
      }
    }
  });

  return families;
}

export function stripTemplateFontRules(css) {
  const raw = String(css || '');
  if (!raw || (!/@font-face/i.test(raw) && !/@import/i.test(raw))) return raw;

  let ast;
  try {
    ast = csstree.parse(raw);
  } catch (err) {
    console.warn('[template-support-css] font rule strip parse failed:', err.message);
    return raw;
  }

  csstree.walk(ast, {
    visit: 'Atrule',
    enter(node, item, list) {
      const name = String(node.name || '').toLowerCase();
      if (TEMPLATE_FONT_AT_RULES.has(name) && item && list) {
        list.remove(item);
      }
    }
  });

  try {
    return csstree.generate(ast);
  } catch (err) {
    console.warn('[template-support-css] font rule strip generate failed:', err.message);
    return raw;
  }
}

// Session-lifetime cache for template _globals.css fetches, keyed by
// `${domain}::fp${fontPolicyVersion}` — the policy changes the emitted bytes,
// so a domain-only key would serve one policy's CSS under the other.
const templateSupportCSSCache = new Map();

// What the font phase did per domain: which families kept their vendored faces
// and which were dropped whole by the all-or-block rule. Read by the exporter
// (warnings) and by tests; a dropped family is a fidelity loss and must never
// be silent.
const templateFontReports = new Map();

/** @returns {{policy: number, kept: object[], dropped: object[]}|null} */
export function getTemplateFontReport(siteName) {
  return templateFontReports.get(String(siteName || '').trim()) || null;
}

/** Test isolation: drop the support-CSS and font-report caches. */
export function _resetTemplateSupportCaches() {
  templateSupportCSSCache.clear();
  templateFontReports.clear();
  templateFontPolicyCache.clear();
  vendoredAssetMapCache.clear();
  buttonVocabularyCache.clear();
}

// Per-domain `_font-policy.json` sidecar. Absent → policy 0 (today's strip),
// which is what keeps every already-published template byte-identical.
const templateFontPolicyCache = new Map();

async function loadTemplateFontPolicy(siteName) {
  if (templateFontPolicyCache.has(siteName)) return templateFontPolicyCache.get(siteName);
  let version = 0;
  try {
    const key = `data/templates/${siteName}/_font-policy.json`;
    // Ask the manifest first: the sidecar is absent for every site not ingested
    // under the policy, and a 404 per template load is noise the deployed
    // builder would print on every load. A null answer (no manifest — local
    // dev, tests) falls through to the fetch.
    const known = await hasManifestAsset(key);
    if (known === false) throw new Error('not in manifest');
    const record = await loadJSON(key);
    const raw = record && Number(record.fontPolicyVersion);
    if (Number.isInteger(raw) && raw > 0) version = raw;
  } catch {
    // Expected-flow fallback: the sidecar exists only for sites ingested with
    // the policy switch on. Its absence IS the answer, not an error.
    version = 0;
  }
  templateFontPolicyCache.set(siteName, version);
  return version;
}

const vendoredAssetMapCache = new Map();

/**
 * The cached map, or `null` when this domain's bundle has not been read yet.
 *
 * A SYNC accessor exists because `assembleSectionsCSS` is synchronous and
 * `assembleDocument` calls it inside a render that cannot await. `null` is a
 * real answer and is never treated as "nothing vendored": the caller falls back
 * to the hotlink, which is what it did before. `loadTemplateSupportCSS` warms
 * this for every rendered domain, and it is awaited before any render, so in
 * practice the map is there.
 */
export function getVendoredAssetMapSync(siteName) {
  const key = typeof siteName === 'string' ? siteName.trim() : '';
  if (!key) return null;
  return vendoredAssetMapCache.get(key) || null;
}

/**
 * THE seam for preferring a vendored byte in section HTML.
 *
 * Every runtime surface that renders template markup calls this and no other:
 * the canvas's three section builders and the saved-preview bundler. One
 * function so the full-render and surgical-edit paths cannot drift — they did
 * for CSS for months, which is how a hero background ended up hotlinked on one
 * path and vendored on the other.
 *
 * SYNC on purpose: two of the three canvas builders are synchronous inside
 * `assembleDocument`. The map must therefore be warm before the render reaches
 * them — `warmVendoredAssetMaps` is what guarantees that. A cold map here is a
 * silent no-op that keeps the hotlink, which is the safe direction but not the
 * intended one, so callers must warm rather than rely on this.
 */
export function rewriteSectionHtmlPreferVendored(html, siteName) {
  const source = String(html || '');
  const domain = typeof siteName === 'string' ? siteName.trim() : '';
  if (!source || !domain) return source;
  const map = getVendoredAssetMapSync(domain);
  if (!map) return source;
  return rewriteHtmlAssetUrlsPreferVendored(source, domain, map, {
    lookupRel: lookupVendoredAssetRel,
    bundleRoot: vendoredBundleRoot,
    toAssetUrl: getAssetURLSync
  });
}

/**
 * Warm every domain a render is about to touch.
 *
 * Deliberately NOT relying on `assembleLiveTemplateCSS` having warmed them: that
 * only loads a map for sections `sectionNeedsTemplateSupportCSS` selects, so a
 * template needing no support CSS would render with a cold map and hotlink every
 * image while every test passed.
 */
export async function warmVendoredAssetMaps(siteNames) {
  const wanted = new Set();
  for (const name of siteNames || []) {
    const clean = typeof name === 'string' ? name.trim() : '';
    if (clean) wanted.add(clean);
  }
  if (wanted.size === 0) return;
  await Promise.all([...wanted].map((name) => ensureVendoredAssetMap(name)));
}

/**
 * Warm this domain's vendored map and return it.
 *
 * The sync accessor cannot distinguish "not read yet" from "nothing vendored" in
 * a way a CALLER can act on — it returns `null` for the first and `{}` for the
 * second, and a caller that treats `null` as empty silently hotlinks. Any
 * surface that CAN await calls this instead, and the HTML rewrite paths do.
 */
export async function ensureVendoredAssetMap(siteName) {
  const key = typeof siteName === 'string' ? siteName.trim() : '';
  if (!key) return {};
  return loadVendoredAssetMap(key);
}

/** Test seam: drop warmed maps so a suite can re-drive the cold path. */
export function _resetVendoredAssetMapCacheForTesting() {
  vendoredAssetMapCache.clear();
}

async function loadVendoredAssetMap(siteName) {
  if (vendoredAssetMapCache.has(siteName)) return vendoredAssetMapCache.get(siteName);
  let map = {};
  try {
    const manifest = await loadJSON(`${vendoredBundleRoot(siteName)}/_manifest.json`);
    if (manifest && typeof manifest.vendoredAssets === 'object' && manifest.vendoredAssets) {
      map = manifest.vendoredAssets;
    }
  } catch {
    // Expected-flow fallback: no reference bundle for this domain. The rewrite
    // then maps nothing, every family blocks, and the drop is reported.
    map = {};
  }
  vendoredAssetMapCache.set(siteName, map);
  return map;
}

async function loadOptionalTextAsset(logicalKey) {
  try {
    if (String(logicalKey).endsWith('/aos.css')) {
      const manifestHasAsset = await hasManifestAsset(logicalKey);
      if (manifestHasAsset === false) return '';
    }
    const resp = await loadAsset(logicalKey);
    return await resp.text();
  } catch {
    return '';
  }
}

export async function loadTemplateSupportCSS(siteName) {
  const key = typeof siteName === 'string' ? siteName.trim() : '';
  if (!key) return '';
  const fontPolicy = await loadTemplateFontPolicy(key);
  // Warm the vendored map for EVERY domain, not only font-policy ones. The map
  // used to be read only inside the policy branch below, so the 371 sites
  // without a policy had no vendored lookup available anywhere — including in
  // `assembleSectionsCSS`, which is synchronous and can only read the cache.
  // It is one already-cached manifest read, and it is what lets a vendored
  // image beat a hotlink.
  await loadVendoredAssetMap(key);
  const cacheKey = `${key}::fp${fontPolicy}`;
  if (templateSupportCSSCache.has(cacheKey)) return templateSupportCSSCache.get(cacheKey);

  const parts = [];
  const globals = await loadOptionalTextAsset(`data/templates/${key}/_globals.css`);
  const styles = globals ? await loadOptionalTextAsset(`data/templates/${key}/_styles.css`) : '';
  if (globals) {
    parts.push(globals);
    // _globals.css strips the template's `:root` font-family vars by convention,
    // but section CSS still references them via var(--bodyFontFamily) etc.
    // Re-introduce just those declarations from _styles.css so per-site font
    // roles (e.g. casaridge.com's Playfair Display titles) resolve.
    const fontVars = extractTemplateFontFamilyVars(styles);
    if (fontVars) parts.push(fontVars);
    const motionStateRules = extractTemplateMotionStateRules(styles);
    if (motionStateRules) parts.push(motionStateRules);
  } else {
    const stylesFallback = await loadOptionalTextAsset(`data/templates/${key}/_styles.css`);
    if (stylesFallback) parts.push(stylesFallback);
  }

  const aos = await loadOptionalTextAsset(`data/templates/${key}/aos.css`);
  if (aos) parts.push(aos);

  const joined = parts.join('\n\n');
  let css;
  if (fontPolicy >= FONT_POLICY_VENDORED) {
    const vendoredAssets = await loadVendoredAssetMap(key);
    const result = rewriteTemplateFontRules(joined, {
      domain: key,
      vendoredAssets
    });
    css = rewriteTemplateAssetUrls(result.css, { domain: key, vendoredAssets });
    templateFontReports.set(key, {
      policy: fontPolicy,
      kept: result.kept,
      dropped: result.dropped
    });
    for (const drop of result.dropped) {
      console.warn(
        `[template-support-css] ${key}: dropped @font-face family "${drop.family}" — ` +
          `${drop.reason} (${drop.urls.join(', ')})`
      );
    }
  } else {
    css = stripTemplateFontRules(joined);
    templateFontReports.set(key, { policy: 0, kept: [], dropped: [] });
  }
  templateSupportCSSCache.set(cacheKey, css || '');
  return css || '';
}

export function buildTemplateSupportBlock(siteName, css) {
  return `${TEMPLATE_SUPPORT_MARKER_PREFIX}${siteName} */\n${String(css || '').trim()}\n/* /pb-template-support:${siteName} */`;
}

// ---------------------------------------------------------------------------
// AOS fallback CSS (Motion Restoration M6)
//
// Eleven templates ship [data-aos] markup with no aos.css in their crawl, so
// their reveal elements have no hiding rule and no transition — visible but
// motionless. The fallback below supplies the missing rules, with two guards:
//   1. Every hiding/offset rule is gated on html[data-pb-motion-runtime] — the
//      handshake the runtime stamps before any class strip (M5) — so content
//      can only be hidden by a runtime that is present to reveal it.
//   2. Emission is scoped to the owning template: a secondary template's
//      fallback rides inside its pb-template-support block (scoped to
//      [data-template-site] by scopeTemplateSupportBlocks), and the primary's
//      carries a :not() guard excluding donor-marked sections, because primary
//      support CSS is otherwise emitted page-wide.
// ---------------------------------------------------------------------------

const aosRuleCheckCache = new Map();

/**
 * True when the CSS already carries AOS rules — detected on the parsed AST
 * ([data-aos] attribute selectors or aos-animate/aos-init class selectors),
 * never by string matching. css-tree parses malformed CSS with recovery, so
 * detection still runs over whatever was salvageable; only an outright parser
 * throw answers `true`, suppressing the fallback and leaving content visible —
 * the safe direction.
 */
export function templateCSSHasAOSRules(css) {
  const raw = String(css || '');
  if (!raw || !raw.includes('aos')) return false;
  let ast;
  try {
    ast = csstree.parse(raw);
  } catch {
    return true;
  }
  let found = false;
  csstree.walk(ast, {
    visit: 'AttributeSelector',
    enter(node) {
      if (selectorAttrName(node) === 'data-aos') found = true;
    }
  });
  if (!found) {
    csstree.walk(ast, {
      visit: 'ClassSelector',
      enter(node) {
        if (node.name === 'aos-animate' || node.name === 'aos-init') found = true;
      }
    });
  }
  return found;
}

function htmlHasAOSMarkup(html) {
  const source = String(html || '');
  if (!source || !source.includes('data-aos')) return false;
  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(`<div>${source}</div>`, 'text/html');
      return !!doc.querySelector('[data-aos]');
    } catch {
      // Fall through to the prefilter answer below.
    }
  }
  // The includes() prefilter already matched; failing open only over-emits
  // handshake-gated rules that match nothing.
  return true;
}

function domainNeedsAOSFallback(domain, rawCSS, sections) {
  const hasMarkup = sections.some(
    (section) =>
      templateSupportAttrValue(section?.templateSiteName) === domain &&
      htmlHasAOSMarkup(section?.html)
  );
  if (!hasMarkup) return false;
  if (!aosRuleCheckCache.has(domain)) {
    aosRuleCheckCache.set(domain, templateCSSHasAOSRules(rawCSS));
  }
  return !aosRuleCheckCache.get(domain);
}

const AOS_FALLBACK_DONOR_GUARD = ':not([data-template-site], [data-template-site] *)';

const AOS_FALLBACK_TYPE_TRANSFORMS = [
  ['fade-up', 'translateY(30px)'],
  ['fade-down', 'translateY(-30px)'],
  ['fade-left', 'translateX(30px)'],
  ['fade-right', 'translateX(-30px)'],
  ['fade-in', 'none'],
  ['slide-left', 'translateX(30px)'],
  ['slide-right', 'translateX(-30px)'],
  ['slide-down', 'translateY(-30px)'],
  ['zoom-out', 'scale(1.1)'],
  ['flip-up', 'perspective(2500px) rotateX(45deg)']
];

const AOS_FALLBACK_DELAYS = [50, 100, 150, 200, 250, 300, 350, 400, 450, 500, 550, 600];
const AOS_FALLBACK_DURATIONS = [400, 600, 800, 900, 1000, 1200];

/**
 * The fallback stylesheet. Covers the animation types actually used across the
 * captured templates. Every rule is gated on the runtime handshake; pass
 * `donorGuard` for the primary-template emission so the page-wide rules cannot
 * reach donor sections from other templates.
 */
export function buildAOSFallbackCSS({ donorGuard = false } = {}) {
  const guard = donorGuard ? AOS_FALLBACK_DONOR_GUARD : '';
  const target = (suffix) => `html[data-pb-motion-runtime] [data-aos]${suffix}${guard}`;
  const rules = [
    '/* pb-aos-fallback — template has [data-aos] markup but no aos.css */',
    `${target('')} {`,
    '  opacity: 0;',
    '  transition-property: transform, opacity;',
    '  transition-duration: 0.8s;',
    '  transition-timing-function: ease;',
    '}',
    `${target('.aos-animate')} { opacity: 1; transform: none; }`
  ];
  for (const [type, transform] of AOS_FALLBACK_TYPE_TRANSFORMS) {
    rules.push(
      `html[data-pb-motion-runtime] [data-aos="${type}"]${guard} { transform: ${transform}; }`
    );
  }
  for (const delay of AOS_FALLBACK_DELAYS) {
    rules.push(
      `${target(`[data-aos-delay="${delay}"].aos-animate`)} { transition-delay: ${delay}ms; }`
    );
  }
  for (const duration of AOS_FALLBACK_DURATIONS) {
    rules.push(
      `${target(`[data-aos-duration="${duration}"]`)} { transition-duration: ${duration}ms; }`
    );
  }
  return rules.join('\n');
}

function collectSectionsForLiveCSS(state) {
  const sections = [];
  if (state?.sharedSections?.navigation) sections.push(state.sharedSections.navigation);
  if (Array.isArray(state?.pages)) {
    for (const page of state.pages) {
      if (Array.isArray(page?.sections)) sections.push(...page.sections);
    }
  }
  if (state?.sharedSections?.footer) sections.push(state.sharedSections.footer);
  return sections;
}

/**
 * True when the project carries explicit root-rule button styling that must
 * override (and therefore strip) the template's own global button rules.
 * Canonical predicate — the single copy shared by export, preview, and the
 * live-CSS assembler. Consumers pass `state.buttonStyles` (not the whole state).
 */
export function hasButtonRootRules(buttonStyles) {
  return !!(
    buttonStyles &&
    (buttonStyles.borderRadius != null ||
      buttonStyles.padding ||
      buttonStyles.backgroundColor ||
      buttonStyles.textColor)
  );
}

/**
 * True when the project carries explicit root-rule form styling. Relocated here
 * from css-assembler.js so `resolveComponentStripPlan` can reference it without
 * importing css-assembler (which imports this module — that direction is a
 * cycle). css-assembler re-exports this name for backward compatibility.
 */
export function hasStateFormRootRules(formStyles) {
  if (!formStyles || typeof formStyles !== 'object') return false;
  return !!(
    FORM_SHAPES.some((preset) => preset.id === formStyles.shape) ||
    FORM_STYLES.some((preset) => preset.id === formStyles.style)
  );
}

// The derived button vocabulary is a pure function of (sections, templateCSS),
// and every render path asks for it through resolveComponentStripPlan. Deriving
// it walks the whole project's HTML and CSS, so memoize on a signature of those
// inputs rather than on `state`, which is a new object on every update.
const buttonVocabularyCache = new Map();
const BUTTON_VOCABULARY_CACHE_LIMIT = 8;

function buttonVocabularySignature(sections, rawTemplateCSS) {
  // Section identity + html/css lengths. Content edits that preserve every
  // length AND the section set cannot change which CLASSES exist, because a
  // class add/remove changes a length. Cheap, and wrong only in ways that
  // cannot alter the answer.
  const parts = [String(rawTemplateCSS || '').length];
  for (const section of sections) {
    parts.push(
      `${section?.instanceId || section?.sectionId || ''}:${(section?.html || '').length}:${(section?.css || '').length}`
    );
  }
  return parts.join('|');
}

function resolveButtonVocabulary(state, rawTemplateCSS) {
  const sections = collectSectionsForLiveCSS(state);
  const signature = buttonVocabularySignature(sections, rawTemplateCSS);
  const cached = buttonVocabularyCache.get(signature);
  if (cached) return cached;
  const classes = deriveButtonClasses(sections, rawTemplateCSS);
  const entry = {
    classes,
    selector: buildButtonSelector(classes),
    // Display-only: what the TEMPLATE animates on its own. Never emitted as
    // CSS — see detectTemplateHoverEffect's docstring.
    templateHoverEffect: detectTemplateHoverEffect(sections, rawTemplateCSS, classes)?.effect || ''
  };
  if (buttonVocabularyCache.size >= BUTTON_VOCABULARY_CACHE_LIMIT) {
    buttonVocabularyCache.clear();
  }
  buttonVocabularyCache.set(signature, entry);
  return entry;
}

/**
 * Single source of truth for which template component-class families to strip
 * from section CSS, and the source domain to scope their replacements. Shared by
 * both the export path (generators.js) and the preview path (iframe-renderer.js)
 * so the two can no longer diverge.
 *
 * Also the one place in the app where `state` and the raw template CSS are BOTH
 * in scope on every rendering path (live preview, flat export, 11ty export,
 * saved-preview bundler), which is why the project's button vocabulary is
 * derived here rather than stored on state: no schema version, no migration,
 * no collab hash surface.
 */
export function resolveComponentStripPlan(state, rawTemplateCSS) {
  const sourceTemplateDomain =
    typeof state?.sourceTemplateDomain === 'string' ? state.sourceTemplateDomain.trim() : '';
  const buttonVocabulary = resolveButtonVocabulary(state, rawTemplateCSS);
  const stripButtons =
    hasButtonRootRules(state?.buttonStyles) ||
    hasGlobalTemplateComponentRules(rawTemplateCSS, TEMPLATE_BUTTON_CLASSES);
  const stripCards = hasGlobalTemplateComponentRules(rawTemplateCSS, TEMPLATE_CARD_CLASSES);
  const stripInputs =
    hasStateFormRootRules(state?.formStyles) ||
    hasGlobalTemplateComponentRules(rawTemplateCSS, TEMPLATE_INPUT_CLASSES);
  const stripPills = hasGlobalTemplateComponentRules(rawTemplateCSS, TEMPLATE_PILL_CLASSES);
  return {
    stripButtons,
    stripCards,
    stripInputs,
    stripPills,
    buttonClasses: buttonVocabulary.classes,
    buttonSelector: buttonVocabulary.selector,
    templateHoverEffect: buttonVocabulary.templateHoverEffect,
    sourceTemplateDomain:
      stripButtons || stripCards || stripInputs || stripPills ? sourceTemplateDomain : ''
  };
}

function buildSecondarySupportStripSets(state, primaryCSS) {
  const sets = [];
  if (
    hasButtonRootRules(state?.buttonStyles) ||
    hasGlobalTemplateComponentRules(primaryCSS, TEMPLATE_BUTTON_CLASSES)
  ) {
    sets.push(TEMPLATE_BUTTON_CLASSES);
  }
  if (hasGlobalTemplateComponentRules(primaryCSS, TEMPLATE_CARD_CLASSES)) {
    sets.push(TEMPLATE_CARD_CLASSES);
  }
  if (hasGlobalTemplateComponentRules(primaryCSS, TEMPLATE_INPUT_CLASSES)) {
    sets.push(TEMPLATE_INPUT_CLASSES);
  }
  if (hasGlobalTemplateComponentRules(primaryCSS, TEMPLATE_PILL_CLASSES)) {
    sets.push(TEMPLATE_PILL_CLASSES);
  }
  return sets;
}

function stripSecondarySupportComponentRules(css, stripSets) {
  let nextCSS = String(css || '');
  for (const classSet of stripSets || []) {
    nextCSS = stripTemplateComponentRules(nextCSS, classSet);
  }
  return nextCSS;
}

function selectorListHasRoot(selectorList) {
  let matched = false;
  if (!selectorList?.children) return matched;
  selectorList.children.forEach((selector) => {
    if (matched) return;
    csstree.walk(selector, {
      visit: 'PseudoClassSelector',
      enter(node) {
        if (node.name === 'root') matched = true;
      }
    });
  });
  return matched;
}

function removePrimaryRootColorDeclarationsFromBlock(block) {
  if (!block?.children?.forEach) return;
  block.children.forEach((child, item) => {
    if (
      child.type === 'Rule' &&
      child.prelude?.type === 'SelectorList' &&
      selectorListHasRoot(child.prelude)
    ) {
      child.block?.children?.forEach((decl, declItem) => {
        if (decl.type !== 'Declaration') return;
        const property = String(decl.property || '');
        if (!THEME_COLOR_KEY_SET.has(property)) return;
        const value = decl.value ? csstree.generate(decl.value) : '';
        if (normalizeHexColor(value)) {
          child.block.children.remove(declItem);
        }
      });
      if (!child.block?.children || child.block.children.isEmpty) {
        block.children.remove(item);
      }
      return;
    }

    if (child.type === 'Atrule' && child.block) {
      removePrimaryRootColorDeclarationsFromBlock(child.block);
      if (child.block.children?.isEmpty) {
        block.children.remove(item);
      }
    }
  });
}

export function stripPrimaryTemplateRootColorVars(css) {
  const raw = String(css || '');
  if (!raw.includes(':root')) return raw;

  let ast;
  try {
    ast = csstree.parse(raw);
  } catch (err) {
    console.warn('[template-support-css] root color strip parse failed:', err.message);
    return raw;
  }

  removePrimaryRootColorDeclarationsFromBlock(ast);

  try {
    return csstree.generate(ast);
  } catch (err) {
    console.warn('[template-support-css] root color strip generate failed:', err.message);
    return raw;
  }
}

/**
 * Derive the combined template support CSS for the given state at call time.
 * Primary domain's _globals.css comes first; secondary domains represented by
 * any rendered section follow as marker-wrapped scoped blocks, sorted.
 * Empty primary domain is allowed — synthetic-starter projects with imported
 * nav/footer still produce secondary blocks.
 *
 * Tests can pass { overrideCSS } to bypass fetches entirely.
 */
export async function assembleLiveTemplateCSS(state, { overrideCSS = null } = {}) {
  if (overrideCSS != null) return String(overrideCSS);
  if (!state || typeof state !== 'object') return '';

  const primaryDomain =
    typeof state.sourceTemplateDomain === 'string' ? state.sourceTemplateDomain.trim() : '';

  const sections = collectSectionsForLiveCSS(state);
  const secondaries = new Set();
  for (const section of sections) {
    const siteName =
      typeof section?.templateSiteName === 'string' ? section.templateSiteName.trim() : '';
    if (!siteName) continue;
    if (siteName === primaryDomain) continue;
    secondaries.add(siteName);
  }

  const primaryRawCSS = primaryDomain ? await loadTemplateSupportCSS(primaryDomain) : '';
  const primaryCSS = stripPrimaryTemplateRootColorVars(
    rewriteCssAssetUrlsPreferVendored(
      primaryRawCSS,
      primaryDomain,
      primaryDomain ? await loadVendoredAssetMap(primaryDomain) : null
    )
  );
  const secondaryStripSets = buildSecondarySupportStripSets(state, primaryCSS);

  // AOS fallback for the primary: page-wide support CSS, so the fallback
  // carries the donor guard rather than a [data-template-site] scope.
  const primaryAOSFallback =
    primaryDomain && domainNeedsAOSFallback(primaryDomain, primaryRawCSS, sections)
      ? buildAOSFallbackCSS({ donorGuard: true })
      : '';

  const primaryParts = [];
  const primaryTrimmed = String(primaryCSS || '').trim();
  if (primaryTrimmed) primaryParts.push(primaryTrimmed);
  if (primaryAOSFallback) primaryParts.push(primaryAOSFallback);
  const primaryOut = primaryParts.join('\n\n');

  if (secondaries.size === 0) return primaryOut;

  const sortedSecondaries = Array.from(secondaries).sort();
  const blocks = await Promise.all(
    sortedSecondaries.map(async (domain) => {
      const raw = await loadTemplateSupportCSS(domain);
      const rewritten = rewriteCssAssetUrlsPreferVendored(
        raw,
        domain,
        await loadVendoredAssetMap(domain)
      );
      const stripped = stripSecondarySupportComponentRules(rewritten, secondaryStripSets);
      // A secondary's fallback rides inside its support block, so the block
      // scoper pins it to [data-template-site="<domain>"] like the rest.
      const fallback = domainNeedsAOSFallback(domain, raw, sections) ? buildAOSFallbackCSS() : '';
      const trimmed = [String(stripped || '').trim(), fallback].filter(Boolean).join('\n\n');
      return trimmed ? buildTemplateSupportBlock(domain, trimmed) : '';
    })
  );

  const parts = [];
  if (primaryOut) parts.push(primaryOut);
  for (const block of blocks) {
    if (block) parts.push(block);
  }
  return parts.join('\n\n');
}
